"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { api, Booking, TrainerListItem } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDeadline, formatMoney } from "@/lib/format";
import { GlobalSlot, groupByDay, loadVenueMap, loadWeekSchedule, partOfDay, PartOfDay } from "@/lib/homeSchedule";
import { Badge, Button, Card } from "@/components/ui";
import { StreakExample } from "@/components/StreakExample";
import { BookingFlow } from "@/components/BookingFlow";

const dayFormatter = new Intl.DateTimeFormat("ru-RU", { weekday: "long", day: "numeric", month: "long" });
const timeFormatter = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" });
const PARTS_OF_DAY: PartOfDay[] = ["утро", "день", "вечер"];
const SLOTS_PER_DAY_SHOWN = 12;

const STEPS = [
  { title: "Выбираете окно", text: "Свободное время видно без регистрации — вместе с ценой и залом." },
  { title: "Бронируете", text: "Слот удерживается 10 минут, пока идёт оплата. Больше трёх удержаний сразу нельзя." },
  { title: "Приходите", text: "Напоминания в Telegram: за сутки, за три часа и утром в день занятия." },
  { title: "Видите результат", text: "Тренер заполняет дневник, вы видите нагрузку и динамику показателей." },
];

const RULES = [
  { label: "Бесплатная отмена", text: "не позднее 4 рабочих часов тренера до начала. Точный дедлайн виден в карточке записи." },
  { label: "Перенос", text: "не позднее 6 рабочих часов. Позже — запрос тренеру, решение за ним." },
  { label: "Возврат приходит двумя частями", text: "для платформы — сразу, для тренера — в течение 5 рабочих дней." },
];

const FAQ = [
  { q: "А если я заболел?", a: "Отмените до дедлайна — деньги вернутся, занятие из блока останется. Отмеченная болезнь не обнулит серию недель." },
  { q: "Можно перенести занятие?", a: "Да, не позднее 6 рабочих часов до начала. После — запрос тренеру: он видит его в своём кабинете и отвечает." },
  { q: "Как возвращаются деньги?", a: "Двумя частями: доля платформы уходит на карту сразу, доля тренера — в течение 5 рабочих дней. Комиссия банка не возвращается." },
  { q: "Зачем анкета здоровья?", a: "Без неё тренер не может оценить допустимость нагрузки. Он видит только отмеченные вами ограничения, данные хранятся отдельно и шифруются." },
  { q: "Кто эти тренеры?", a: "Каждый работает как ИП, регистрация проверена администратором. Оплата делится между платформой и тренером сразу на стороне банка." },
  { q: "Что если тренер отменит?", a: "Полный возврат независимо от срока, и платформа контролирует, чтобы деньги вернулись вовремя." },
];

function TrainerCard({ trainer, weekCount }: { trainer: TrainerListItem; weekCount: number }) {
  return (
    <Card className="flex h-full flex-col p-4">
      <div className="mb-3 aspect-[4/3] rounded-lg bg-gradient-to-br from-knowledge/20 to-ink/10" />
      <div className="mb-1 font-display text-base font-bold text-text">{trainer.display_name}</div>
      {trainer.bio && <p className="mb-2 text-sm text-text-muted">{trainer.bio}</p>}
      {trainer.qualifications && <p className="mb-2 text-xs text-text-muted">{trainer.qualifications}</p>}
      {trainer.venues.length > 0 && (
        <p className="mb-4 text-xs text-text-muted">{trainer.venues.join(" · ")} · 60 минут, индивидуально</p>
      )}
      <div className="mt-auto space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div>
            <div className="text-xs text-text-muted">Занятие от</div>
            <div className="font-semibold text-text">{trainer.min_price ? formatMoney(trainer.min_price) : "—"}</div>
          </div>
          {weekCount > 0 && <Badge variant="brand">{weekCount} окон на неделе</Badge>}
        </div>
        <Link href={`/trainers/${trainer.user_id}`}>
          <Button variant="secondary" className="w-full">
            Расписание и цены
          </Button>
        </Link>
      </div>
    </Card>
  );
}

export default function HomePage() {
  const { me, isTrainer } = useAuth();
  const [trainers, setTrainers] = useState<TrainerListItem[]>([]);
  const [venueMap, setVenueMap] = useState<Map<string, string>>(new Map());
  const [weekSlots, setWeekSlots] = useState<GlobalSlot[]>([]);
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [error, setError] = useState(false);
  const [venueFilter, setVenueFilter] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<PartOfDay | null>(null);
  const [activeSlot, setActiveSlot] = useState<GlobalSlot | null>(null);

  useEffect(() => {
    api
      .get<TrainerListItem[]>("/trainers")
      .then(async (t) => {
        setTrainers(t);
        const [venueMapResult, slots] = await Promise.all([loadVenueMap(api, t), loadWeekSchedule(api, t)]);
        setVenueMap(venueMapResult);
        setWeekSlots(slots);
      })
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    if (me && !isTrainer) {
      api.get<Booking[]>("/bookings/my?role=student").then(setBookings).catch(() => setBookings(null));
    }
  }, [me, isTrainer]);

  const venueName = (id: string) => venueMap.get(id) ?? "";

  const filteredSlots = useMemo(
    () =>
      weekSlots.filter(
        (s) => (!venueFilter || venueName(s.venue_id) === venueFilter) && (!timeFilter || partOfDay(s.start_at) === timeFilter)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekSlots, venueFilter, timeFilter, venueMap]
  );

  const dayGroups = useMemo(() => groupByDay(filteredSlots), [filteredSlots]);
  const involvedVenues = useMemo(
    () => Array.from(new Set(weekSlots.map((s) => venueName(s.venue_id)).filter(Boolean))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekSlots, venueMap]
  );
  const trainerCount = new Set(weekSlots.map((s) => s.trainer_id)).size;
  const nearest = weekSlots.length > 0 ? [...weekSlots].sort((a, b) => a.start_at.localeCompare(b.start_at))[0] : null;
  const weekCountByTrainer = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of weekSlots) map.set(s.trainer_id, (map.get(s.trainer_id) ?? 0) + 1);
    return map;
  }, [weekSlots]);

  return (
    <div className="space-y-16">
      <section className="grid gap-6 rounded-2xl bg-gradient-to-br from-brand/10 via-brand/5 to-transparent p-6 lg:grid-cols-[1.3fr_1fr] lg:items-center lg:p-8">
        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-text-muted">Персональные тренировки</div>
          <h1 className="mb-4 font-display text-4xl font-extrabold leading-tight text-text sm:text-5xl">Не сорвать цепочку</h1>
          <p className="mb-6 max-w-lg text-text-muted">
            Регулярность важнее интенсивности. Одно и то же время каждую неделю, напоминания вовремя и дневник каждой
            тренировки — чтобы занятия не отменялись сами собой.
          </p>
          <div className="mb-4 flex flex-wrap gap-3">
            {nearest ? (
              <Button onClick={() => setActiveSlot(nearest)}>Записаться на {formatDeadline(nearest.start_at)}</Button>
            ) : (
              <Link href="#trainers">
                <Button>Выбрать тренера</Button>
              </Link>
            )}
            <Link href="#how-it-works">
              <Button variant="secondary">Как это работает</Button>
            </Link>
          </div>
        </div>

        <Card className="p-5">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Свободно на этой неделе</span>
            <Badge variant="success">сейчас</Badge>
          </div>
          <div className="mb-4 flex items-baseline gap-2">
            <span className="font-display text-4xl font-extrabold text-text">{weekSlots.length}</span>
            <span className="text-sm text-text-muted">окон у {trainerCount || trainers.length} тренеров</span>
          </div>
          <div className="space-y-2 border-t border-border pt-3 text-sm">
            <div className="flex justify-between">
              <span className="text-text-muted">Ближайшее</span>
              <span className="font-medium text-text">{nearest ? formatDeadline(nearest.start_at) : "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-muted">Залы</span>
              <span className="font-medium text-text">{involvedVenues.join(", ") || "—"}</span>
            </div>
          </div>
        </Card>
      </section>

      <section>
        <h2 className="mb-1 font-display text-2xl font-bold text-text">Ближайшие окна</h2>
        <p className="mb-4 text-sm text-text-muted">Реальное расписание всех тренеров. Нажмите на время — откроется оформление.</p>

        <div className="mb-6 flex flex-wrap gap-2">
          {involvedVenues.map((v) => (
            <button key={v} onClick={() => setVenueFilter(venueFilter === v ? null : v)}>
              <Badge variant={venueFilter === v ? "brand" : "neutral"}>{v}</Badge>
            </button>
          ))}
          {PARTS_OF_DAY.map((p) => (
            <button key={p} onClick={() => setTimeFilter(timeFilter === p ? null : p)}>
              <Badge variant={timeFilter === p ? "brand" : "neutral"}>{p}</Badge>
            </button>
          ))}
        </div>

        {error && <p className="text-sm text-risk">Не удалось связаться с сервером.</p>}

        <div className="space-y-6">
          {dayGroups.map(([day, slots]) => (
            <div key={day}>
              <h3 className="mb-2 text-sm font-medium capitalize text-text">
                {dayFormatter.format(new Date(`${day}T00:00:00Z`))}
              </h3>
              <div className="flex flex-wrap gap-2">
                {slots.slice(0, SLOTS_PER_DAY_SHOWN).map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveSlot(s)}
                    className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text transition-colors hover:border-brand/50"
                  >
                    <span className="font-medium">{timeFormatter.format(new Date(s.start_at))}</span>{" "}
                    <span className="text-text-muted">{s.trainerName}</span>{" "}
                    <span className="text-text-muted">· {s.price ? formatMoney(s.price) : "—"}</span>
                  </button>
                ))}
                {slots.length > SLOTS_PER_DAY_SHOWN && (
                  <span className="self-center text-sm text-text-muted">и ещё {slots.length - SLOTS_PER_DAY_SHOWN}</span>
                )}
              </div>
            </div>
          ))}
          {dayGroups.length === 0 && !error && <p className="text-sm text-text-muted">На эту неделю свободных окон нет.</p>}
        </div>
      </section>

      <section id="trainers">
        <h2 className="mb-1 font-display text-2xl font-bold text-text">Тренеры</h2>
        <p className="mb-6 text-sm text-text-muted">
          Пилот идёт с небольшим кругом тренеров: каждого мы знаем лично, у каждого проверена регистрация ИП, а
          фотографии публикует администратор, а не сам тренер.
        </p>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {trainers.map((t) => (
            <TrainerCard key={t.user_id} trainer={t} weekCount={weekCountByTrainer.get(t.user_id) ?? 0} />
          ))}
          {trainers.length === 0 && !error && <p className="text-sm text-text-muted">Пока нет подтверждённых тренеров.</p>}
        </div>
      </section>

      <section id="how-it-works" className="border-t border-border pt-12">
        <h2 className="mb-8 font-display text-2xl font-bold text-text">Как это работает</h2>
        <div className="mb-8 grid gap-8 sm:grid-cols-4">
          {STEPS.map((step, i) => (
            <div key={step.title} className="relative border-l-2 border-border pl-4 first:border-brand">
              <div className="mb-2 font-display text-sm font-bold text-brand">{i + 1}</div>
              <div className="mb-1 font-medium text-text">{step.title}</div>
              <p className="text-sm text-text-muted">{step.text}</p>
            </div>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {RULES.map((r) => (
            <div key={r.label} className="rounded-lg bg-knowledge/10 p-4 text-sm">
              <div className="mb-1 font-medium text-text">{r.label}</div>
              <p className="text-text-muted">{r.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.3fr_1fr] lg:items-center">
        <div>
          <h2 className="mb-4 font-display text-2xl font-bold leading-snug text-text">
            Результат даёт не отдельная тренировка, а непрерывная серия недель
          </h2>
          <p className="mb-4 text-text-muted">
            Люди бросают не потому, что тяжело, а потому что один раз перенесли, второй раз забыли, а на третий стало
            неловко возвращаться. Поэтому платформа устроена вокруг постоянного занятия: одно и то же время каждую
            неделю резервируется за вами автоматически, а пропуск с указанной причиной не обнуляет серию.
          </p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="brand">постоянное занятие</Badge>
            <Badge variant="waiting">три напоминания</Badge>
            <Badge variant="knowledge">дневник каждой тренировки</Badge>
          </div>
        </div>
        <StreakExample />
      </section>

      <section>
        <h2 className="mb-6 font-display text-2xl font-bold text-text">Частые вопросы</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {FAQ.map((item) => (
            <div key={item.q}>
              <div className="mb-1 font-medium text-text">{item.q}</div>
              <p className="text-sm text-text-muted">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="flex flex-col gap-2 border-t border-border pt-6 text-xs text-text-muted sm:flex-row sm:items-center sm:justify-between">
        <span>Оплата на защищённой странице банка · данные о здоровье хранятся отдельно и шифруются</span>
        <span>Политика данных · Оферта · Анкета</span>
      </footer>

      {activeSlot && (
        <BookingFlow
          slot={activeSlot}
          trainerName={activeSlot.trainerName}
          venueName={venueName(activeSlot.venue_id)}
          price={activeSlot.price}
          onClose={() => setActiveSlot(null)}
          onBooked={() => {
            setActiveSlot(null);
            if (trainers.length) loadWeekSchedule(api, trainers).then(setWeekSlots);
          }}
        />
      )}
    </div>
  );
}
